import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, Sparkles, Award, Building2, Handshake, TrendingUp,
  ClipboardCheck, Landmark, BookOpen, Trophy, Globe2, Users, Radar,
} from "lucide-react";

/**
 * Phase XV — Government Executive Canvas (Whole-of-Government)
 * Tabs: Canvas · Strategy Centre · Programmes · Treasury · Benchmarking ·
 * Development Partners · Cabinet Decisions · Demonstration.
 */
const GOLD = "#C99A2E";
const PURPLE = "#7B2FBE";
const GREEN = "#6BB88A";
const BLUE = "#4FA3D1";
const TEAL = "#2E7D6B";
const AMBER = "#E4A93C";
const RED = "#E06C6C";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const auth = () => {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Card = ({ title, accent, right, children, testId }) => (
  <div className="rounded-sm border" style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }} data-testid={testId}>
    <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: accent + "25" }}>
      <div className="w-1 h-4 rounded-sm" style={{ background: accent }} />
      <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{title}</div>
      <div className="ml-auto">{right}</div>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const Chip = ({ children, color = BLUE, filled }) => (
  <span className="inline-flex items-center rounded-sm px-1.5 py-0.5 font-mono uppercase text-[9.5px] tracking-[0.14em]"
        style={{ background: filled ? color : color + "20", color: filled ? "#071B2B" : color, border: "1px solid " + color + "45" }}>
    {children}
  </span>
);

export const GovernmentExecutiveCanvas = () => {
  const [tab, setTab] = useState("canvas");
  const [canvas, setCanvas] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [programmes, setProgrammes] = useState(null);
  const [treasury, setTreasury] = useState(null);
  const [bench, setBench] = useState(null);
  const [partners, setPartners] = useState(null);
  const [decisions, setDecisions] = useState({ items: [] });
  const [scenarios, setScenarios] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [decisionForm, setDecisionForm] = useState({
    title: "", minister_responsible: "Minister of Finance", national_impact: "", risk: "medium",
  });

  useEffect(() => {
    (async () => {
      try {
        const [c, s, p, t, b, d, dec, sc] = await Promise.all([
          axios.get(`${API}/cadastre360/phase-xv/executive-canvas`,   { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xv/strategy-centre`,     { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xv/programmes`,          { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xv/treasury`,            { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xv/benchmarking`,        { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xv/development-partners`,{ headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xv/cabinet-decisions`,   { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xv/demonstration/scenarios`,{ headers: auth() }),
        ]);
        setCanvas(c.data); setStrategy(s.data); setProgrammes(p.data); setTreasury(t.data);
        setBench(b.data); setPartners(d.data); setDecisions(dec.data); setScenarios(sc.data);
      } catch (e) { toast.error("Failed to load canvas — " + e.message); }
    })();
  }, []);

  const createDecision = async () => {
    if (!decisionForm.title.trim()) { toast.error("Title required"); return; }
    try {
      await axios.post(`${API}/cadastre360/phase-xv/cabinet-decisions`, decisionForm, { headers: auth() });
      const dec = await axios.get(`${API}/cadastre360/phase-xv/cabinet-decisions`, { headers: auth() });
      setDecisions(dec.data);
      setDecisionForm({ ...decisionForm, title: "", national_impact: "" });
      toast.success("Cabinet decision recorded");
    } catch (e) { toast.error("Failed — " + e.message); }
  };

  const setDecisionStatus = async (id, status) => {
    try {
      await axios.post(`${API}/cadastre360/phase-xv/cabinet-decisions/${id}/action`,
        { status, note: "Set from canvas" }, { headers: auth() });
      const dec = await axios.get(`${API}/cadastre360/phase-xv/cabinet-decisions`, { headers: auth() });
      setDecisions(dec.data);
      toast.success(`Status → ${status}`);
    } catch (e) { toast.error("Failed — " + e.message); }
  };

  const openScenario = async (key) => {
    try {
      const r = await axios.get(`${API}/cadastre360/phase-xv/demonstration/scenarios/${key}`, { headers: auth() });
      setSelectedScenario(r.data);
    } catch (e) { toast.error("Scenario failed — " + e.message); }
  };

  const TABS = [
    { key: "canvas",     label: "Whole-of-Gov Canvas",       icon: LayoutDashboard, accent: GOLD },
    { key: "strategy",   label: "National Strategy Centre",  icon: Trophy,          accent: PURPLE },
    { key: "programmes", label: "Programme Management",      icon: ClipboardCheck,  accent: BLUE },
    { key: "treasury",   label: "Treasury & Investment",     icon: Landmark,        accent: GOLD },
    { key: "bench",      label: "Government Benchmarking",   icon: TrendingUp,      accent: GREEN },
    { key: "partners",   label: "Development Partners",      icon: Handshake,       accent: BLUE },
    { key: "decisions",  label: "Cabinet Decisions",         icon: Award,           accent: PURPLE },
    { key: "demo",       label: "Demonstration Mode",        icon: Sparkles,        accent: GREEN },
  ];

  return (
    <div className="p-6 space-y-4" data-testid="page-executive-canvas">
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: GOLD + "60",
                     background: `linear-gradient(120deg, rgba(7,27,43,0.95) 0%, rgba(11,53,88,0.85) 45%, ${GOLD}30 100%)` }}>
        <LayoutDashboard size={38} className="mt-1" style={{ color: GOLD }} />
        <div>
          <div className="font-mono uppercase tracking-[0.32em] text-[10px]" style={{ color: GOLD }}>
            Cadastre360™ · Phase XV · Whole-of-Government Executive Canvas™
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#F6F8FA] mt-1">
            Government Executive Canvas
          </h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-4xl leading-relaxed">
            Whole-of-government executive intelligence · National Strategy · Programmes ·
            Treasury · Benchmarking · Development Partners · Cabinet Decisions · Demonstration Mode.
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  className={"px-3 py-1.5 text-[11.5px] flex items-center gap-1.5 font-medium border-b-2 " +
                             (tab === t.key ? "" : "border-transparent text-[#7C93A5]")}
                  style={tab === t.key ? { borderColor: t.accent, color: "#F6F8FA" } : {}}
                  data-testid={`canvas-tab-${t.key}`}>
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "canvas" && canvas && (
        <Card title="Whole-of-Government Executive Canvas · 15 panels" accent={GOLD} testId="canvas-panels">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {canvas.panels.map((p) => {
              const val = p.unit === "USD" ? `$${Number(p.value).toLocaleString()}`
                        : p.unit === "score" ? p.value
                        : Number(p.value).toLocaleString();
              return (
                <div key={p.key} className="rounded-sm border px-2 py-1.5" style={{ borderColor: GOLD + "40", background: "rgba(7,27,43,0.55)" }}
                     data-testid={`canvas-panel-${p.key}`}>
                  <div className="font-mono uppercase text-[9.5px] tracking-[0.16em] text-[#7C93A5]">{p.label}</div>
                  <div className="text-[14px] font-bold text-[#F6F8FA]">{val}</div>
                  <div className="text-[10px] text-[#B3C2CC]">{p.trend >= 0 ? "▲" : "▼"} {Math.abs(p.trend)}% · {p.unit}</div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {tab === "strategy" && strategy && (
        <>
          <Card title="National Strategy Centre™" accent={PURPLE} testId="canvas-strategy">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {strategy.strategies.map((s) => (
                <div key={s.key} className="rounded-sm border p-2" style={{ borderColor: PURPLE + "35", background: "rgba(7,27,43,0.55)" }}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="text-[13px] font-semibold text-[#F6F8FA] flex-1">{s.label}</div>
                    <Chip color={PURPLE}>{s.linked_domain}</Chip>
                  </div>
                  <div className="h-1.5 rounded-sm bg-white/5 mb-1">
                    <div className="h-full rounded-sm" style={{ width: `${s.progress}%`, background: PURPLE }} />
                  </div>
                  <div className="text-[10.5px] text-[#B3C2CC]">{s.progress}% · target {s.target_year} · Ministries: {s.linked_ministries.join(", ")}</div>
                </div>
              ))}
            </div>
          </Card>
          <Card title="Ministerial KPIs · Outcome scores" accent={GREEN}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
              {strategy.ministerial_kpis.map((k) => (
                <div key={k.ministry} className="rounded-sm border px-2 py-1" style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
                  <div className="text-[11px] text-[#F6F8FA]">{k.ministry}</div>
                  <div className="text-[13px] font-bold" style={{ color: GREEN }}>{k.outcome_score}</div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {tab === "programmes" && programmes && (
        <Card title={`Government Programme Management™ · ${programmes.programmes.length} programmes`} accent={BLUE} testId="canvas-programmes">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[640px] overflow-auto">
            {programmes.programmes.map((p) => (
              <div key={p.id} className="rounded-sm border p-2" style={{ borderColor: BLUE + "30", background: "rgba(7,27,43,0.55)" }}
                   data-testid={`programme-${p.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-[12.5px] font-semibold text-[#F6F8FA] flex-1">{p.programme}</div>
                  <Chip color={GOLD}>{p.ministry}</Chip>
                </div>
                <div className="text-[10.5px] text-[#B3C2CC]">Funding: ${p.funding_usd.toLocaleString()} · Performance {p.performance_pct}% · Completion {p.completion_pct}%</div>
                <div className="text-[10.5px] text-[#7C93A5]">Value delivered: {p.value_delivered} · Risks: {p.risks.join(", ")}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "treasury" && treasury && (
        <>
          <Card title={`Treasury & Investment Centre™ · pipeline USD ${(treasury.total_usd/1_000_000_000).toFixed(2)}B`} accent={GOLD} testId="canvas-treasury">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
              {treasury.pipeline.map((p) => (
                <div key={p.category} className="rounded-sm border px-2 py-1.5" style={{ borderColor: GOLD + "40", background: "rgba(7,27,43,0.55)" }}>
                  <div className="font-mono uppercase text-[9.5px] text-[#7C93A5] tracking-[0.16em]">{p.category}</div>
                  <div className="text-[14px] font-bold text-[#F6F8FA]" style={{ color: GOLD }}>${(p.amount_usd/1_000_000).toFixed(0)}M</div>
                </div>
              ))}
            </div>
          </Card>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card title="Economic Impact" accent={GREEN}>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-sm border px-2 py-1.5" style={{ borderColor: GREEN + "40", background: "rgba(7,27,43,0.5)" }}>
                  <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">GDP contribution</div>
                  <div className="text-[14px] font-bold text-[#F6F8FA]">{treasury.economic_impact.gdp_contribution_pct}%</div>
                </div>
                <div className="rounded-sm border px-2 py-1.5" style={{ borderColor: GREEN + "40", background: "rgba(7,27,43,0.5)" }}>
                  <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">Jobs supported</div>
                  <div className="text-[14px] font-bold text-[#F6F8FA]">{treasury.economic_impact.jobs_supported.toLocaleString()}</div>
                </div>
              </div>
            </Card>
            <Card title="Revenue Snapshot" accent={BLUE}>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-sm border px-2 py-1.5" style={{ borderColor: BLUE + "40", background: "rgba(7,27,43,0.5)" }}>
                  <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">Collected</div>
                  <div className="text-[14px] font-bold text-[#F6F8FA]">${treasury.revenue_snapshot.collected_usd.toLocaleString()}</div>
                </div>
                <div className="rounded-sm border px-2 py-1.5" style={{ borderColor: BLUE + "40", background: "rgba(7,27,43,0.5)" }}>
                  <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">Outstanding</div>
                  <div className="text-[14px] font-bold text-[#F6F8FA]">${treasury.revenue_snapshot.outstanding_usd.toLocaleString()}</div>
                </div>
              </div>
            </Card>
          </div>
        </>
      )}

      {tab === "bench" && bench && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card title="Provincial comparison" accent={GREEN} testId="canvas-bench-provincial">
            {bench.provincial.map((p) => (
              <div key={p.province} className="flex items-center gap-2 py-1 text-[12px]">
                <div className="w-40 text-[#F6F8FA] truncate">{p.province}</div>
                <div className="flex-1 h-1.5 rounded-sm bg-white/5">
                  <div className="h-full rounded-sm" style={{ width: `${p.score}%`, background: GREEN }} />
                </div>
                <div className="w-8 text-right font-mono text-[11px] text-[#F6F8FA]">{p.score}</div>
              </div>
            ))}
          </Card>
          <Card title="Ministry comparison" accent={GOLD}>
            {bench.ministry.map((m) => (
              <div key={m.ministry} className="flex items-center gap-2 py-1 text-[12px]">
                <div className="w-40 text-[#F6F8FA] truncate">{m.ministry}</div>
                <div className="flex-1 h-1.5 rounded-sm bg-white/5">
                  <div className="h-full rounded-sm" style={{ width: `${m.score}%`, background: GOLD }} />
                </div>
                <div className="w-8 text-right font-mono text-[11px] text-[#F6F8FA]">{m.score}</div>
              </div>
            ))}
          </Card>
          <Card title="Historical maturity" accent={BLUE}>
            <div className="flex items-end gap-2 h-24 mt-2">
              {bench.historical.map((h) => (
                <div key={h.year} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full rounded-sm" style={{ background: BLUE, height: `${h.national_maturity}%` }} />
                  <div className="text-[10px] text-[#B3C2CC]">{h.year}</div>
                  <div className="text-[10px] text-[#F6F8FA] font-mono">{h.national_maturity}</div>
                </div>
              ))}
            </div>
          </Card>
          <Card title="Regional & international" accent={PURPLE}>
            <div className="font-mono uppercase text-[9.5px] text-[#7C93A5] mb-1">Regional</div>
            {bench.regional.map((r) => (
              <div key={r.country} className="text-[11.5px] text-[#F6F8FA]">{r.country}: <span className="font-mono">{r.score}</span></div>
            ))}
            <div className="font-mono uppercase text-[9.5px] text-[#7C93A5] mt-2 mb-1">International frameworks</div>
            {bench.international.map((i) => (
              <div key={i.framework} className="text-[11.5px] text-[#F6F8FA]">{i.framework}: <span className="font-mono">{i.score}</span> (peer {i.peer})</div>
            ))}
          </Card>
        </div>
      )}

      {tab === "partners" && partners && (
        <Card title="Development Partner Workspaces™" accent={BLUE} testId="canvas-partners">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {partners.partners.map((p) => (
              <div key={p.key} className="rounded-sm border p-2" style={{ borderColor: BLUE + "35", background: "rgba(7,27,43,0.55)" }}
                   data-testid={`partner-${p.key}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Handshake size={14} style={{ color: BLUE }} />
                  <div className="text-[12.5px] font-semibold text-[#F6F8FA] flex-1">{p.label}</div>
                  <Chip color={GREEN}>{p.compliance_score}</Chip>
                </div>
                <div className="text-[10.5px] text-[#B3C2CC]">Focus: {p.focus}</div>
                <div className="text-[10.5px] text-[#7C93A5]">Indicators {p.relevant_indicators} · Active reports {p.active_reports}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "decisions" && (
        <>
          <Card title="Record Cabinet Decision" accent={PURPLE} testId="canvas-decision-form">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <input value={decisionForm.title} onChange={(e) => setDecisionForm({...decisionForm, title: e.target.value})}
                     placeholder="Decision title" data-testid="decision-title"
                     className="md:col-span-2 rounded-sm border bg-black/30 px-2 py-1.5 text-[12px] text-[#F6F8FA] border-white/15" />
              <input value={decisionForm.minister_responsible} onChange={(e) => setDecisionForm({...decisionForm, minister_responsible: e.target.value})}
                     placeholder="Minister responsible" data-testid="decision-minister"
                     className="rounded-sm border bg-black/30 px-2 py-1.5 text-[12px] text-[#F6F8FA] border-white/15" />
              <select value={decisionForm.risk} onChange={(e) => setDecisionForm({...decisionForm, risk: e.target.value})}
                      className="rounded-sm border bg-black/30 px-2 py-1.5 text-[12px] text-[#F6F8FA] border-white/15">
                <option value="low">Low risk</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
              <input value={decisionForm.national_impact} onChange={(e) => setDecisionForm({...decisionForm, national_impact: e.target.value})}
                     placeholder="National impact" data-testid="decision-impact"
                     className="md:col-span-3 rounded-sm border bg-black/30 px-2 py-1.5 text-[12px] text-[#F6F8FA] border-white/15" />
              <button onClick={createDecision} className="rounded-sm border px-3 py-1.5 text-[11.5px]"
                      style={{ borderColor: PURPLE + "60", color: "#F6F8FA", background: PURPLE + "25" }}
                      data-testid="btn-record-decision">
                <Sparkles size={12} className="inline mr-1" /> Record
              </button>
            </div>
          </Card>
          <Card title={`Cabinet Decisions · ${decisions.items?.length || 0}`} accent={PURPLE} testId="canvas-decisions-list">
            {decisions.items?.length === 0 && <div className="text-[11px] text-[#7C93A5]">No decisions yet — record one above.</div>}
            <div className="space-y-1.5 max-h-[500px] overflow-auto">
              {decisions.items?.map((d) => (
                <div key={d.id} className="rounded-sm border p-2" style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.5)" }}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="text-[12.5px] font-semibold text-[#F6F8FA] flex-1">{d.title}</div>
                    <Chip color={PURPLE}>{d.status}</Chip>
                    <Chip color={d.risk === "high" ? RED : d.risk === "medium" ? AMBER : GREEN}>{d.risk}</Chip>
                  </div>
                  <div className="text-[10.5px] text-[#B3C2CC]">Minister: {d.minister_responsible} · Completion {d.completion_pct}%</div>
                  {d.national_impact && <div className="text-[10.5px] text-[#7C93A5] italic">{d.national_impact}</div>}
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {["in_progress", "approved", "closed", "escalated"].map((st) => (
                      <button key={st} onClick={() => setDecisionStatus(d.id, st)}
                              className="rounded-sm border px-2 py-0.5 text-[10px]"
                              style={{ borderColor: BLUE + "50", color: BLUE }}
                              data-testid={`decision-action-${st}-${d.id}`}>
                        {st}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {tab === "demo" && scenarios && (
        <>
          <Card title="National Demonstration Mode™" accent={GREEN} testId="canvas-demo">
            <div className="text-[11.5px] text-[#DCE3E8] mb-2">{scenarios.notice}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {scenarios.scenarios.map((s) => (
                <button key={s.key} onClick={() => openScenario(s.key)}
                        className="rounded-sm border px-2 py-2 text-left"
                        style={{ borderColor: GREEN + "40", background: "rgba(7,27,43,0.55)" }}
                        data-testid={`scenario-${s.key}`}>
                  <div className="text-[12.5px] font-semibold text-[#F6F8FA]">{s.label}</div>
                  <div className="text-[10.5px] text-[#B3C2CC]">{s.audience}</div>
                </button>
              ))}
            </div>
          </Card>
          {selectedScenario && (
            <Card title={selectedScenario.label} accent={GREEN}>
              <div className="font-mono uppercase text-[9.5px] text-[#7C93A5] mb-1">Highlights</div>
              <ul className="text-[11.5px] text-[#F6F8FA] list-disc pl-4 mb-2">
                {selectedScenario.highlights.map((h) => <li key={h}>{h}</li>)}
              </ul>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {selectedScenario.kpis.map((k) => (
                  <div key={k.label} className="rounded-sm border px-2 py-1" style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
                    <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">{k.label}</div>
                    <div className="text-[13px] font-bold text-[#F6F8FA]">{k.value}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default GovernmentExecutiveCanvas;
